// Downloads an HLS (.m3u8) video by fetching every segment and writing them, in playback order,
// to a single file the browser can save. The backend serves both the media playlist and the .ts
// segments from the same origin (no CORS, no auth), and segment URIs resolve relative to the
// playlist URL — exactly how video.js loads them for playback — so if a clip plays, it downloads.
//
// Game clips can be full recordings (1GB+), so when the File System Access API is available we
// stream segments straight to disk and never hold the whole video in memory. Browsers without it
// fall back to buffering into a Blob.

export interface HlsDownloadProgress {
  completed: number;
  total: number;
}

// How many segments to fetch in parallel. Game clips can have hundreds of short .ts segments;
// a small pool keeps the download fast without opening one request per segment at once.
const SEGMENT_CONCURRENCY = 6;

// Minimal typing for the File System Access API (not in the default DOM lib).
interface FileSystemWritableStreamLike {
  write(data: BufferSource | Blob): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}
interface FileSystemFileHandleLike {
  createWritable(): Promise<FileSystemWritableStreamLike>;
}
type ShowSaveFilePicker = (options?: {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}) => Promise<FileSystemFileHandleLike>;

// An ordered byte sink the download writes segments to. close() finalizes the saved file.
interface DownloadSink {
  write(chunk: ArrayBuffer): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

// Thrown when the user dismisses the save dialog — the caller treats this as a quiet cancel.
export class DownloadCanceledError extends Error {
  constructor() {
    super("Download canceled");
    this.name = "DownloadCanceledError";
  }
}

function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

// File extension (and MIME type) for the saved clip. Concatenated MPEG-TS segments stay .ts;
// fragmented-MP4 segments produce a playable .mp4.
function mediaTypeFor(uriOrName: string): { extension: "mp4" | "ts"; mime: string } {
  return /\.(mp4|m4s)(\?|$)/i.test(uriOrName)
    ? { extension: "mp4", mime: "video/mp4" }
    : { extension: "ts", mime: "video/mp2t" };
}

export function hlsDownloadExtension(playlistUrl: string): "mp4" | "ts" {
  return mediaTypeFor(playlistUrl).extension;
}

// Opens a save destination. Prefers the File System Access API (streams to disk, bounded memory);
// when unavailable, returns a sink that buffers chunks and saves a Blob on close.
// Must be called from a user gesture so the native save dialog is allowed to open.
async function createDownloadSink(filename: string, mime: string): Promise<DownloadSink> {
  const showSaveFilePicker = (window as unknown as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker;

  if (typeof showSaveFilePicker === "function") {
    let handle: FileSystemFileHandleLike;
    try {
      handle = await showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: "Video", accept: { [mime]: [`.${mediaTypeFor(filename).extension}`] } }],
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw new DownloadCanceledError();
      throw err;
    }
    const writable = await handle.createWritable();
    return {
      write: (chunk) => writable.write(chunk),
      close: () => writable.close(),
      abort: async () => {
        try {
          await writable.abort?.();
        } catch {
          /* best-effort */
        }
      },
    };
  }

  // Fallback: accumulate in memory, then trigger a normal browser download on close.
  const chunks: ArrayBuffer[] = [];
  return {
    write: async (chunk) => {
      chunks.push(chunk);
    },
    close: async () => saveBlob(new Blob(chunks, { type: mime }), filename),
    abort: async () => {
      chunks.length = 0;
    },
  };
}

// Pull the ordered list of segment URIs out of a media playlist, resolved to absolute URLs
// against the playlist's own location. Includes the optional EXT-X-MAP init segment (fMP4).
function parseSegmentUris(playlistText: string, playlistUrl: string): string[] {
  const uris: string[] = [];
  for (const rawLine of playlistText.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith("#")) {
      const mapMatch = line.match(/^#EXT-X-MAP:.*URI="([^"]+)"/);
      if (mapMatch) uris.push(new URL(mapMatch[1], playlistUrl).href);
      continue;
    }
    uris.push(new URL(line, playlistUrl).href);
  }
  return uris;
}

export interface HlsPreflightResult {
  ok: boolean;
  reason?: string;
}

// Cheap up-front check that an HLS clip is actually playable: the media playlist loads and its
// first segment is reachable. This catches the "every segment 404s/403s" case (e.g. a backend
// that can't resolve the segment URLs, or expired access) so the UI can explain why a clip won't
// play immediately, instead of waiting for the player to stall. Only the first segment is probed,
// and its body is aborted as soon as the status is known, so this costs about one small request.
export async function preflightHlsPlayable(playlistUrl: string, signal?: AbortSignal): Promise<HlsPreflightResult> {
  let playlistResponse: Response;
  try {
    playlistResponse = await fetch(playlistUrl, { signal });
  } catch {
    // An aborted fetch (e.g. the card unmounted) isn't a real failure — don't flag the clip.
    return signal?.aborted ? { ok: true } : { ok: false, reason: "Couldn't reach the video." };
  }
  if (!playlistResponse.ok) {
    return { ok: false, reason: `Couldn't load the video playlist (${playlistResponse.status}).` };
  }

  const segmentUris = parseSegmentUris(await playlistResponse.text(), playlistUrl);
  if (segmentUris.length === 0) {
    return { ok: false, reason: "This video isn't ready to play yet." };
  }

  // Probe only the first segment, and abort the body once we have the status — we want the
  // reachability check, not the bytes.
  const probe = new AbortController();
  const onParentAbort = () => probe.abort();
  signal?.addEventListener("abort", onParentAbort);
  try {
    const segmentResponse = await fetch(segmentUris[0], { signal: probe.signal });
    const ok = segmentResponse.ok;
    const status = segmentResponse.status;
    probe.abort();
    return ok ? { ok: true } : { ok: false, reason: `This video's segments are unavailable (${status}).` };
  } catch {
    return signal?.aborted ? { ok: true } : { ok: false, reason: "This video's segments are unavailable." };
  } finally {
    signal?.removeEventListener("abort", onParentAbort);
  }
}

export async function downloadHlsVideo(
  playlistUrl: string,
  filename: string,
  onProgress: (progress: HlsDownloadProgress) => void,
  signal?: AbortSignal
): Promise<void> {
  const sink = await createDownloadSink(filename, mediaTypeFor(playlistUrl).mime);

  try {
    const playlistResponse = await fetch(playlistUrl, { signal });
    if (!playlistResponse.ok) {
      throw new Error(`Couldn't load the video playlist (${playlistResponse.status})`);
    }

    const segmentUris = parseSegmentUris(await playlistResponse.text(), playlistUrl);
    if (segmentUris.length === 0) {
      throw new Error("This video has no downloadable segments yet.");
    }

    let completed = 0;
    let nextIndex = 0;
    let nextToWrite = 0;
    const pending = new Map<number, ArrayBuffer>();
    // Writes to the sink must be serialized; chaining keeps them in order and one-at-a-time.
    let writeChain: Promise<void> = Promise.resolve();

    // Flush any already-fetched segments that are next in line, draining the reorder buffer so
    // memory stays bounded to the concurrency window rather than the whole video.
    const flushInOrder = (): Promise<void> => {
      writeChain = writeChain.then(async () => {
        while (pending.has(nextToWrite)) {
          const buffer = pending.get(nextToWrite)!;
          pending.delete(nextToWrite);
          await sink.write(buffer);
          nextToWrite += 1;
        }
      });
      return writeChain;
    };

    const worker = async (): Promise<void> => {
      for (;;) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        const index = nextIndex++;
        if (index >= segmentUris.length) return;

        const segmentResponse = await fetch(segmentUris[index], { signal });
        if (!segmentResponse.ok) {
          throw new Error(`Failed downloading segment ${index + 1} of ${segmentUris.length} (${segmentResponse.status})`);
        }

        pending.set(index, await segmentResponse.arrayBuffer());
        completed += 1;
        onProgress({ completed, total: segmentUris.length });
        await flushInOrder();
      }
    };

    onProgress({ completed: 0, total: segmentUris.length });
    await Promise.all(Array.from({ length: Math.min(SEGMENT_CONCURRENCY, segmentUris.length) }, worker));
    await writeChain;
    await sink.close();
  } catch (err) {
    await sink.abort();
    throw err;
  }
}
